import { useEffect, useMemo } from "react";
import { Alert, Grid, Switch, Table, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FlagOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import GeneralTableHeader from "../common/general-table-header/GeneralTableHeader";
import { selectIsKlearlySuperAdmin } from "../access-control/accountSlice";
import {
  FeatureFlagKey,
  FeatureFlagSource,
  IResolvedFeatureFlag,
} from "../../interfaces/featureFlag.interface";
import {
  FeatureFlagUpdateError,
  fetchFeatureFlags,
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsLoading,
  selectUpdatingFeatureFlagKeys,
  updateFeatureFlag,
} from "./featureFlagsSlice";
import styles from "./FeatureFlags.module.css";

interface IFeatureFlagRow extends IResolvedFeatureFlag {
  key: string;
}

// A flip is served from an in-process cache on every backend container, so it
// converges rather than applying instantly. See the backend's
// shared/services/db-private-feature-flags/AGENTS.md → "Cache".
const PROPAGATION_NOTE = "A flip reaches every service within about 2 minutes.";

export default function FeatureFlags() {
  const dispatch = useAppDispatch();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const flags = useAppSelector(selectFeatureFlags);
  const loading = useAppSelector(selectFeatureFlagsLoading);
  const loadError = useAppSelector(selectFeatureFlagsError);
  const updatingKeys = useAppSelector(selectUpdatingFeatureFlagKeys);
  const isSuperAdmin = useAppSelector(selectIsKlearlySuperAdmin);

  useEffect(() => {
    dispatch(fetchFeatureFlags());
  }, [dispatch]);

  const rows: IFeatureFlagRow[] = useMemo(
    () =>
      Object.entries(flags ?? {})
        .map(([key, resolved]) => ({ key, ...resolved }))
        .sort((a, b) => a.key.localeCompare(b.key)),
    [flags]
  );

  const onToggle = async (row: IFeatureFlagRow, enabled: boolean) => {
    try {
      await dispatch(updateFeatureFlag({ key: row.key, enabled })).unwrap();
      message.success(`${row.key} is now ${enabled ? "on" : "off"}`);
    } catch (err) {
      const error = err as FeatureFlagUpdateError | undefined;
      message.error(
        error?.status === 403
          ? "Only a Klearly Super Admin can change a feature flag."
          : error?.message || `Failed to update ${row.key}`
      );
    }
  };

  const columns: ColumnsType<IFeatureFlagRow> = useMemo(
    () => [
      {
        title: "Flag",
        key: "flag",
        render: (_: unknown, row: IFeatureFlagRow) => (
          <div className={styles.flag_cell}>
            <div className={styles.flag_row}>
              <span className={styles.flag_key}>{row.key}</span>
              {row.key === FeatureFlagKey.HideUnreleasedAcquirers &&
                row.enabled && (
                  <span className={styles.unreleased_tag}>
                    Rapyd + Shift4 hidden
                  </span>
                )}
            </div>
            <div className={styles.flag_description}>{row.description}</div>
          </div>
        ),
      },
      {
        title: "Source",
        dataIndex: "source",
        key: "source",
        width: 140,
        render: (source: FeatureFlagSource) => (
          <Tooltip
            title={
              source === FeatureFlagSource.Database
                ? "Someone has flipped this flag; the stored value overrides the code default."
                : "Never flipped — resolving to the code default."
            }
          >
            <span
              className={`${styles.source} ${
                source === FeatureFlagSource.Database
                  ? styles.source_database
                  : styles.source_registry
              }`}
            >
              {source}
            </span>
          </Tooltip>
        ),
      },
      {
        title: "State",
        dataIndex: "enabled",
        key: "enabled",
        width: 88,
        align: "center" as const,
        render: (enabled: boolean, row: IFeatureFlagRow) => (
          <Switch
            checked={enabled}
            disabled={!isSuperAdmin}
            loading={updatingKeys.includes(row.key)}
            onChange={(next) => onToggle(row, next)}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSuperAdmin, updatingKeys]
  );

  return (
    <div>
      <GeneralTableHeader
        title="Feature Flags"
        description="Flip the private feature flags this stage resolves. Changes apply to every service, no deploy needed."
        avatarUrl={<FlagOutlined style={{ fontSize: "30px" }} />}
      />

      <p className={styles.propagation}>{PROPAGATION_NOTE}</p>

      {!isSuperAdmin && (
        <Alert
          className={styles.notice}
          type="info"
          showIcon
          message="Read-only"
          description="Flipping a flag requires the Klearly Super Admin role."
        />
      )}

      {loadError && (
        <Alert
          className={styles.notice}
          type="error"
          showIcon
          message="Could not load the feature flags"
          description={`${loadError} — reload the page to try again.`}
        />
      )}

      <div className={styles.board}>
        <Table<IFeatureFlagRow>
          rowKey="key"
          dataSource={rows}
          loading={loading}
          columns={columns}
          size={isMobile ? "small" : "middle"}
          pagination={false}
          footer={() => (
            <div className={styles.footer}>
              {isSuperAdmin
                ? "Super Admin only."
                : "Read-only. Super Admin only."}
            </div>
          )}
        />
      </div>
    </div>
  );
}
